// Plan-ProdagiHelpWidget-CustomerDashboard.md Phase 2 (2026-05-13)
//
// Embeds the Prodagi singleton help agent as a floating chat launcher on
// the authenticated customer dashboard. Mirrors the third-party
// widget.js embed pattern that customers use on their own websites —
// meta-consistent UX.
//
// Gated by:
//   - User must be authenticated (no widget on /login or /signup)
//   - VITE_PRODAGI_GUIDE_AGENT_ID env var must be set (no-op in dev
//     when env is missing — see front/.env.example for setup)
//
// Backend leverage:
//   The cross-DB resolver in `back/api/widget.py:_resolve_widget_agent`
//   transparently falls back from `prodagi_ai.agents` to
//   `prodagi_ai_admin.prodagi_agents`, so the same /api/widget/* surface
//   works for this admin singleton. No new endpoints.
//
// Lifecycle:
//   - Mounts on login (user changes from null → set)
//   - Re-mounts on user switch (effect depends on user?.id)
//   - Unmounts on logout — cleanup removes the script, the
//     #prodagi-widget-container div, and the window.ProdagiWidget global
//     so the launcher disappears immediately.

import React, { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

const AGENT_ID = import.meta.env.VITE_PRODAGI_GUIDE_AGENT_ID as
  | string
  | undefined;
// Bugfix 2026-05-13 — the customer dashboard at `app.prodagi.ai` serves
// widget.js from its own origin, but the API lives on `help.prodagi.ai`.
// widget.js's `apiBaseUrl` defaults to the script origin (line 12 of
// public/widget.js), so without an explicit override the config fetch
// hits `app.prodagi.ai/api/widget/config/...` → 404 → silent fallback
// to `launcher_icon_type: 'default'` (default messenger SVG instead of
// the admin-uploaded custom launcher icon). Pin the API origin via
// `data-api-url` so the config fetch reaches the real backend.
//
// Third-party customer embeds load widget.js directly from
// `help.prodagi.ai` so they have script-origin === api-origin and don't
// need this override — only the customer-dashboard embed has the split.
const API_URL = import.meta.env.VITE_API_URL as string | undefined;

const ProdagiHelpWidget: React.FC = () => {
  const { user } = useAuth();

  useEffect(() => {
    if (!user || !AGENT_ID) return;

    const widgetSrc = `${window.location.origin}/widget.js`;

    const script = document.createElement('script');
    script.src = widgetSrc;
    script.setAttribute('data-agent-id', AGENT_ID);
    script.setAttribute('data-position', 'bottom-right');
    if (API_URL) {
      // Pins widget.js's `apiBaseUrl` to the backend origin (see comment
      // on API_URL above). widget.js strips the trailing slash itself.
      script.setAttribute('data-api-url', API_URL);
    } else {
      // Don't abort the mount — widget.js still falls through to the
      // script origin for both the API and the iframe URL, so the
      // launcher will at least open if the React /widget/<id> route
      // resolves locally. But the launcher icon + most config fields
      // will be defaults; surface the cause loudly in console so the
      // operator notices the missing env var in dev.
      console.warn(
        '[ProdagiHelpWidget] VITE_API_URL is unset — widget will fetch ' +
          'config from window.origin, which only works when the script ' +
          'origin matches the API origin (e.g., help.prodagi.ai). On ' +
          'app.prodagi.ai the config fetch will 404 and the launcher ' +
          'will render the default messenger icon.',
      );
    }
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup: user logged out OR component unmounts. Remove the
      // launcher container + the script tag + the window global so a
      // subsequent login re-runs the bootstrap cleanly.
      script.remove();
      document.getElementById('prodagi-widget-container')?.remove();
      delete (window as unknown as { ProdagiWidget?: unknown }).ProdagiWidget;
    };
  }, [user?.id]);

  return null;
};

export default ProdagiHelpWidget;
